import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserMinus, RotateCcw } from 'lucide-react';
import { api } from '@/api/client';
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';

/**
 * LA COLA DE DESCARTE (B6).
 *
 * De cada 100 chats de Osito compran ~15. Los otros 85 terminan acá.
 *
 * Esta pantalla existe por una razón concreta: **un descarte que nadie puede
 * mirar es indistinguible de un lead perdido por un filtro mal calibrado.** Si
 * los cuatro indicadores empiezan a escalar de menos, el único síntoma es que
 * esta lista crece — y sin pantalla, nadie lo nota hasta contar las ventas que
 * no entraron.
 *
 * Es la contraparte del reporte de cierre: ahí sale el número, acá están las
 * filas que lo produjeron (CANON D1: todo número se puede abrir y comprobar).
 */

interface FilaDescarte {
  conversationId: string;
  contactId: string;
  nombre: string;
  telefono: string;
  intentos: number;
  descartadoAt: string | null;
  motivo: string | null;
}

const fecha = (iso: string | null): string =>
  iso
    ? new Intl.DateTimeFormat('es-DO', { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(iso),
      )
    : '—';

export function Descartes() {
  const qc = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['descartes'],
    queryFn: () => api.get<FilaDescarte[]>('/api/descartes'),
  });

  const rescatar = useMutation({
    mutationFn: (contactId: string) =>
      api.post<{ rescatado: boolean }>(`/api/descartes/${contactId}/rescatar`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['descartes'] }),
  });

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Descartados</CardTitle>
          <CardDescription>
            Clientes a los que se les escribió dos veces y no contestaron. No están
            bloqueados: si vuelven a escribir, el sistema los saca de acá solo y les
            devuelve sus dos intentos. Si esta lista crece de golpe, puede que el filtro
            esté dejando pasar ventas — vale la pena revisar un par de conversaciones.
          </CardDescription>
        </CardHeader>

        <CardBody>
          {isLoading && (
            <div className="space-y-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-10 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800"
                />
              ))}
            </div>
          )}

          {isError && (
            <EmptyState
              title="No se pudo cargar la cola"
              description="Reintenta en un momento."
              action={<Button onClick={() => void refetch()}>Reintentar</Button>}
            />
          )}

          {!isLoading && !isError && (data?.length ?? 0) === 0 && (
            <EmptyState
              icon={UserMinus}
              title="Nadie descartado todavía"
              description="Cuando un cliente no conteste después de dos intentos, aparecerá acá."
            />
          )}

          {!isLoading && !isError && (data?.length ?? 0) > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-slate-400 dark:border-slate-800">
                  <tr>
                    <th className="py-2 pr-4">Cliente</th>
                    <th className="py-2 pr-4">Teléfono</th>
                    <th className="py-2 pr-4">Intentos</th>
                    <th className="py-2 pr-4">Descartado</th>
                    <th className="py-2 pr-4">Motivo</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {data!.map((f) => (
                    <tr
                      key={f.conversationId}
                      className="border-b border-surface-border last:border-0 dark:border-slate-800"
                    >
                      <td className="py-2 pr-4 font-medium">{f.nombre || 'Sin nombre'}</td>
                      <td className="py-2 pr-4 tabular-nums text-slate-500">{f.telefono || '—'}</td>
                      <td className="py-2 pr-4 tabular-nums">{f.intentos}</td>
                      <td className="py-2 pr-4 text-slate-500">{fecha(f.descartadoAt)}</td>
                      <td className="py-2 pr-4 text-slate-500">{f.motivo ?? '—'}</td>
                      <td className="py-2 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={rescatar.isPending}
                          onClick={() => rescatar.mutate(f.contactId)}
                          title="Lo saca del descarte y le devuelve sus dos intentos"
                        >
                          <RotateCcw className="mr-1 h-4 w-4" />
                          Rescatar
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
